<script>
import StoreService from '@/services/StoreService'
import MarketplaceCategories from '@/components/MarketplaceComponents/MarketplaceCategories'
import Breadcrumbs from '@/components/MarketplaceComponents/Breadcrumbs.vue'
import PageTitle from '@/components/MarketplaceComponents/PageTitle.vue'
import Pagination from '@/components/Pagination/Pagination.vue'
import ProductCard from '@/components/MarketplaceComponents/ProductCard'
import { mapGetters, mapState } from 'vuex'
import { createMeta } from '~/utils/meta'

export default {
  components: {
    MarketplaceCategories,
    Breadcrumbs,
    Pagination,
    ProductCard,
    PageTitle
  },

  async asyncData ({ $axios, route }) {
    const { data } = await StoreService.getMarketplaceAppByPath($axios, route.params.app)

    const application = data?.data

    const productsData = await StoreService.getMarketplaceProductsWithUserID($axios, 1, 12, '', false, application.categories.map(c => c.id))

    const products = productsData?.data?.data?.products
    const productsCount = +productsData?.data?.data?.count?.[0]?.count

    return {
      application,
      products,
      productsCount
    }
  },

  data () {
    return {
      application: null,
      products: null,
      productsCount: 0,
      page: 0,
      pageSize: 12
    }
  },

  computed: {
    ...mapState('user', ['user']),
    ...mapGetters('user', ['isAdmin']),

    activeTabId () {
      return this.application?.app_type || this.tabs[0].id
    },

    appPath () {
      return this.$route.params.app
    },

    currentTabTitle () {
      return this.tabs?.find(tab => tab.id === this.activeTabId)?.title
    },

    appName () {
      return this.application?.title || ''
    },

    breadcrumbs () {
      return [
        { title: this.$t('marketplace'), to: '/marketplace' },
        {
          title: this.appName
        }
      ]
    },

    goBackPath () {
      return '/'
    },

    categories () {
      return this.application?.categories
    },

    categoriesWithItems () {
      return this.categories?.filter(c => c.count > 0 || this.isAdmin)
    },

    h1Title () {
      let title = this.$t('appItems', { name: this.appName })
      if (this.$i18n.locale === 'ru') {
        switch (this.appPath) {
          case 'steam':
            title = 'Пополнение баланса Steam'
            break
          case 'playstation':
            title = 'Пополнение PS Store, Подписка PSN'
            break
          case 'xbox':
            title = 'Купить Xbox Game Pass, Live Cards'
            break
          case 'nintendo':
            title = 'Карты пополнения eShop Nintendo'
            break
          case 'chatgpt':
            title = 'Подписка Chat GPT Plus'
            break
          case 'app-store-and-itunes':
            title = 'Карты пополнение Apple ID'
            break
          case 'discord':
            title = 'Товары Discord'
            break
          case 'microsoft-windows':
            title = 'Товары Microsoft'
            break
          case 'clash-royale':
            title = 'Товары Clash Royale'
            break
          case 'clash-of-clans':
            title = 'Игровые товары Clash of Clans'
            break
          case 'valorant':
            title = 'Игровые товары Valorant'
            break
          case 'fortnite':
            title = 'Игровые товары Fortnite'
            break
          case 'mobile-legends':
            title = 'Mobile Legends: Игровые товары'
            break
          case 'pubg-mobile':
            title = 'PUBG Mobile: Игровые товары'
            break
          case 'rise-of-kingdoms':
            title = 'Rise of Kingdoms: Игровые товары'
            break
          case 'roblox':
            title = 'Roblox: Игровые товары'
            break
          case 'world-of-tanks-blitz':
            title = 'World of Tanks Blitz: Игровые товары'
            break
          case 'hay-day':
            title = 'Hay Day: Игровые товары'
            break
          case 'free-fire':
            title = 'Free Fire: Игровые товары'
            break
          case 'standoff-2':
            title = 'Товары по Standoff 2'
            break
          case 'hades-ii':
            title = 'Товары по Hades II'
            break
          case 'gta-5':
            title = 'Ключи GTA 5 (Grand Theft Auto V)'
            break
          case 'scum':
            title = 'Ключи активации SCUM'
            break
          case 'honkai-star-rail':
            title = 'Товары и услуги Honkai Star Rail'
            break
          case 'blockman-go':
            title = 'Blockman GO'
            break
          case 'brawl-stars':
            title = 'Brawl Stars: Игровые товары'
            break
        }
      }
      return title
    },

    isSeller () {
      return this.user?.is_seller
    },

    pagesCount () {
      if (!this.productsCount || this.productsCount === 0) {
        return 1
      }

      return Math.ceil(this.productsCount / this.pageSize)
    }
  },

  watch: {
    async appPath () {
      await this.loadApp()
      this.page = 0
      this.loadProducts()
    },

    pageSize () {
      this.page = 0
      this.loadProducts()
    },

    page () {
      this.loadProducts()
    }
  },

  methods: {
    async loadApp () {
      const { data } = await StoreService.getMarketplaceAppByPath(this.$axios, this.appPath)

      this.application = data?.data
    },

    async loadProducts () {
      const { data } = await StoreService.getMarketplaceProductsWithUserID(this.$axios, this.page + 1, this.pageSize, '', false, this.application?.categories?.map(c => c.id))

      this.products = data?.data?.products
      this.productsCount = +data?.data?.count?.[0]?.count
    },

    categoryName (category) {
      return category.translations?.[this.$i18n.locale.split('-')[0]]?.category_name || this.$t(category.category_name?.trim())
    },

    productDescription (product) {
      if (product.short_description && product.short_description !== '') {
        return product.short_description
      }

      return product.description
    },

    appWithCategoryLink (category) {
      return this.localeLocation(`/marketplace/${this.appPath}/${category?.seo_data?.path}`)
    },

    addNewService () {
      this.$router.push(this.localeLocation(`/marketplace/add?type=${this.activeTabId}&app=${this.application.id}&category=${this.currentCategory}`))
    }
  },

  head () {
    if (this.$i18n.locale === 'ru') {
      let title = this.$t('applicationPageMetaTitle', {
        name: this.application?.title || '',
        categories: this.application?.categories?.map(c => this.$t(c.category_name))?.join(', ')
      })
      let description = this.$t('applicationPageMetaDescription', {
        name: this.application?.title || '',
        categories: this.application?.categories?.map(c => this.$t(c.category_name))?.join(', ')
      })
      switch (this.appPath) {
        case 'steam':
          title = 'Пополнение баланса Steam аккаунта, игрового кошелька Стим банковской картой'
          description = 'Пополнение баланса Steam с минимальной комиссией. Пополнить стим аккаунт в России, Казахстан, СНГ быстро и безопасно любой банковской картой на Gaming Goods.'
          break
        case 'playstation':
          title = 'Пополнение кошелька PS Store, PSN онлайн банковской картой'
          description = 'Пополнить PS Store: Турция, США, Индия, Польша, Арабские эмираты и другие. Купить подписку Playstation Network с оплатой банковской картой в России.'
          break
        case 'xbox':
          title = 'Пополнение баланса xbox, покупка Game Pass TRY, BL, US: оплата картой онлайн'
          description = 'Коды пополнение баланса Xbox для покупки игр. Купить подписку Game Pass Unlimited, Xbox Live Gold, Gift Card для хбокс или PC аккаунтов Турция, Бразилия, США навсегда.'
          break
        case 'nintendo':
          title = 'Пополнение баланса eShop Nintendo — Gaming Goods'
          description = 'Вы можете пополнить баланс eShop Nintendo Switch из России на любую сумму онлайн, оплатив банковской картой на маркетплейсе Gaming Goods.'
          break
        case 'chatgpt':
          title = 'Купить подписку Chat GPT | Доступ GPT Plus'
          description = 'Покупайте подписку ИИ ChatGPT от OpenAI по минимальным ценам с быстрой доставкой. Оплачивайте подписку GPT Plus в России онлайн банковской картой на Gaming Goods.'
          break
        case 'app-store-and-itunes':
          title = 'Подарочные карты пополнение Apple ID'
          description = 'Вы можете купить карты пополнения Apple Itunes на любую сумму по выгодным ценам. Моментальное зачисление после оплаты, Gaming Goods.'
          break
        case 'discord':
          title = 'Купить Discord Nitro выгодно'
          description = 'Оформите подписку Дискорд Нитро в России с моментальной активацией. Получите Анимированные эмодзи. Full HD стриминг Дискорд.'
          break
        case 'microsoft-windows':
          title = 'Купить ключ windows 10/11, microsoft office'
          description = 'Вы можете купить ключ Windows 11, программу Microsoft Office на маркетплейсе Gaming Goods без ограничений из России по выгодным ценам.'
          break
        case 'clash-royale':
          title = 'Clash Royale: Гемы, Пасс, Акции'
          description = 'Вы можете купить гемы, пасс и акции для игры Клеш Рояль по выгодным ценам на маркетплейсе Gaming Goods. Безопасная сделка.'
          break
        case 'clash-of-clans':
          title = 'Купить в Clash of Clans: гемы, акции, пасс и другой донат'
          description = 'Покупайте гемы, боевой пропуск, баттл пасс, акции и золото для игры Клеш оф Кланс по выгодным ценам на маркетплейсе Gaming Goods. Безопасная сделка.'
          break
        case 'valorant':
          title = 'Valorant: коды на пополнения Valorant Points'
          description = 'Купить коды пополнения вп, прайм Валорант по выгодным ценам в России без ограничений на маркетплейсе Gaming Goods с безопасная сделкой.'
          break
        case 'fortnite':
          title = 'Fortnite | Купить коды на В баксы пополнение и ресурсы'
          description = 'Покупайте коды пополнения V Bucks в любом количестве и другие товары Фортнайт по выгодным ценам в России без ограничений на маркетплейсе Gaming Goods. Безопасная сделка.'
          break
        case 'mobile-legends':
          title = 'Пополнение аккаунта и покупки в Mobile Legends'
          description = 'Вы можете купить алмазы, скины, сделать пополнение в мобайл легенд из России без ограничений по низким ценам на маркетплейсе Gaming Goods.'
          break
        case 'pubg-mobile':
          title = 'PUBG Mobile: Покупка и продажа UC, Лута'
          description = 'Вы можете купить игровую валюту UC, Лут Metro Royale, оружие и другие предметы Пабг Мобал по низким ценам на маркетплейсе Gaming Goods. Моментальное пополнение.'
          break
        case 'rise-of-kingdoms':
          title = 'Rise of Kingdoms: Купить, самоцветы, ресурсы, игровые наборы'
          description = 'Купить наборы, кристаллы, ресурсы райс оф кингдомс по низким ценам в любом количестве вы можете на маркетплейсе Gaming Goods. Моментальное зачисление, безопасная сделка.'
          break
        case 'roblox':
          title = 'Roblox: Купить робуксы, вещи, премиум, карты'
          description = 'Покупайте Robux, вещи, премиум, gift карты Роблокс по низким ценам на маркетплейсе Gaming Goods. Моментальное зачисление, безопасная сделка.'
          break
        case 'world-of-tanks-blitz':
          title = 'World of Tanks Blitz: Донат, золото, пропуска'
          description = 'Вы можете купить золото, пропуска и любой другой донат ВОТ Блиц по низким ценам на маркетплейсе Gaming Goods. Моментальное зачисление Ворлд оф Танк Блиц.'
          break
        case 'hay-day':
          title = 'Hay Day: купить, продать алмазы и донат'
          description = 'Вы можете купить или продать алмазы, донат, фарм пасс Хей Дей через маркетплейс Gaming Goods по выгодным ценам и гарантией безопасности.'
          break
        case 'free-fire':
          title = 'Free Fire: купить алмазы, пропуск, донат'
          description = 'Купить алмазы Фри Фаер, пропуск, донат дешево в России. Пополняйте Гарана шоп с моментальным зачислением на маркетплейсе Gaming Goods.'
          break
        case 'standoff-2':
          title = 'Купить или продать скины, ножи, перчатки Standoff 2'
          description = 'Покупайте скины и другие товары мобильной игры Стендофф 2 с гарантией. Станьте продавцем скинов и других предметов Standoff 2 на маркетплейсе цифровых товаров Gaming Goods с простомы выводом денег.'
          break
        case 'hades-ii':
          title = 'Купить Hades 2 на ПК с активацией — Gaming Goods'
          description = 'Покупайте и продавайте ключи, услуги активации, Оффлайн аккаунты, онлайн Аккаунты или сдавайте в аренду аккаунты по Hades II на маркетплейсе Gaming-Goods!'
          break
        case 'gta-5':
          title = 'Купить GTA 5 ключи, steam gift на ПК, Xbox, PS в России — Gaming Goods'
          description = 'Покупайте ключи активации различных версиий и обновлений ГТА 5 с гарантией у проверенных продавцов. Продавайте ключи GTA 5 и зарабатывайте став продавцом Gaming goods.'
          break
        case 'scum':
          title = 'Купить ключи активации аккуаунта для игры SCUM в Steam — Gaming goods'
          description = 'Покупайте ключи стим игры SCUM с гарантией на маркетплейсе. У нас проверенные продавцы и реальные отзывы. Покупайте игру СКАМ на Gaming goods.'
          break
        case 'honkai-star-rail':
          title = 'Купить донат, сущности, боевой проуск Honkai Star Rail  — Gaming goods'
          description = 'Покупка продажа в Хонкай Стар Рейл сущностей древних снов, боевой пропуск и другие доанты с гарантией. У нас проверенные продавцы и реальные отзывы. Маркетплейс Gaming goods.'
          break
        case 'blockman-go':
          title = 'Донат, GCubes, кланы, предметы в игре Blockman GO'
          description = 'Покупайте, продавайте обменивайте игровые товары Блокман го. Маркетплейс Gaming Goods является гарантом безопасной сделки. Зарабатывайте помогая игрокам покупать товары, донат в игре Блокамн ГО.'
          break
        case 'brawl-stars':
          title = 'Покупки в Brawl Stars: Гемы, донат, квесты'
          description = 'Покупайте любые гемы и другие донаты Бравл старс. Продавайте гемы, донат, услуги и прочее в Браво старс с бесплатной регистрацией на Gaming Goods.'
          break
      }

      return createMeta.call(this, {
        title,
        description
      })
    }
    return createMeta.call(this, {
      title: this.$t('applicationPageMetaTitle', {
        name: this.application?.title || '',
        categories: this.application?.categories?.map(c => this.$t(c.category_name))?.join(', ')
      }),
      description: this.$t('applicationPageMetaDescription', {
        name: this.application?.title || '',
        categories: this.application?.categories?.map(c => this.$t(c.category_name))?.join(', ')
      })
    })
  }
}
</script>

<template>
  <div :class="$style.page">
    <MarketplaceCategories is-outside one-line />

    <Breadcrumbs :list="breadcrumbs" />

    <div v-if="application" :class="$style.page__body">
      <PageTitle with-go-back :go-back-path="goBackPath" :title="h1Title" />

      <Button v-if="isSeller" is-primary :class="$style.seller" @click="addNewService">
        {{ $t('createService') }}
      </Button>

      <div :class="$style.tags">
        <NuxtLink v-for="category in categoriesWithItems" :key="category.id" :class="$style.tag" :to="appWithCategoryLink(category)">
          {{ categoryName(category) }} ({{ category.count }})
        </NuxtLink>
      </div>

      <div v-if="products" :class="$style.products">
        <div v-for="product in products" :key="product.id">
          <ProductCard :product="product" short with-category-text with-seller-text />
        </div>
      </div>

      <Pagination
        :class="$style.pagination"
        :per-page="pageSize"
        :pages-count="pagesCount"
        :current-page="page"
        @perPageChange="(pp) => { pageSize = pp }"
        @pageChange="(p) => { page = p }"
      />
    </div>
  </div>
</template>

<style lang="scss" module>
.page {
  width: 100%;

  &__body {
    padding: 16px;
  }
}

.seller {
  width: 100% !important;
  margin-bottom: 24px;
}

.tags {
  display: flex;
  flex-wrap: wrap;
}

.tag {
  padding: 8px 12px;
  background: $blue-grey50;
  color: $white;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  margin-right: 8px;
  margin-bottom: 8px;
  border: 1px solid $blue-grey50;
  cursor: pointer;

  &:last-child {
    margin-right: 0;
  }
}

.products {
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: 8px;
  margin-top: 24px;

  @media (min-width: $breakpointTablet) {
    grid-template-columns: 1fr 1fr;
  }

  @media (min-width: $breakpointDesktopWide) {
    grid-template-columns: 1fr 1fr 1fr 1fr;
  }
}

.product {
  display: block;
  height: 100%;
  background: $blue-grey70;
  color: $white;
  padding: 12px;
  border-radius: 8px;
  cursor: pointer;

  &__description {
    width: 100%;
    overflow: hidden;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;
    color: $white;
    font-size: 14px;
    font-weight: 400;
    line-height: 20px;
    padding-top: 12px;
    margin-top: 12px;
    border-top: 1px solid $blue-grey40;
  }

  &__dealer {
    display: grid;
    grid-template-columns: max-content 1fr;
    grid-gap: 8px;

    &Right {
      display: grid;
      grid-template-columns: 1fr;
      grid-gap: 4px;

      &Top {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 4px;
      }

      &Bottom {
        display: flex;
        align-items: center;
        justify-content: space-between;
      }
    }

    &Name {
      color: $white;
      font-size: 16px;
      font-weight: 500;
      line-height: 20px;
    }

    &Avatar {
      width: 48px;
      height: 48px;
      border-radius: 50%;
      object-fit: cover;
    }

    &Reviews {
      color: $blue-grey10;
      font-size: 12px;
      font-weight: 400;
      line-height: 20px;
    }

    &Price {
      color: $white;
      font-size: 14px;
      font-weight: 500;
      line-height: 20px;
    }
  }
}

.pagination {
  margin: 40px 0;
}
</style>
